'use client';

// Module-level in-flight promise dedup + short-TTL cache for client-side GET
// requests that are called from many components per page load (e.g. hooks
// used in 5+ components that all fire fetches in their own useEffect).
//
// Usage:
//   const data = await dedupedFetchJson('/api/foo');
// After a successful mutation, invalidate:
//   invalidateDedupedFetch('/api/foo');
// On auth-state changes (logout), wipe everything:
//   invalidateAllDedupedFetches();

const inFlight = new Map<string, Promise<unknown>>();
const cache = new Map<string, { value: unknown; expiry: number }>();

export interface DedupedFetchOptions {
  // Cache TTL in ms. 0 = no caching (only in-flight dedup). Default 60_000.
  ttlMs?: number;
  // Custom RequestInit. Default `{credentials: 'include'}`.
  init?: RequestInit;
  // Treat non-2xx as error (throw). Default true.
  throwOnError?: boolean;
}

export async function dedupedFetchJson<T = unknown>(
  url: string,
  options: DedupedFetchOptions = {},
): Promise<T> {
  const { ttlMs = 60_000, init, throwOnError = true } = options;

  const cached = cache.get(url);
  if (cached && cached.expiry > Date.now()) {
    return cached.value as T;
  }

  const pending = inFlight.get(url);
  if (pending) return pending as Promise<T>;

  const promise = (async () => {
    try {
      const res = await fetch(url, { credentials: 'include', ...init });
      if (!res.ok && throwOnError) {
        throw new Error(`Fetch ${url} -> HTTP ${res.status}`);
      }
      const data: T = await res.json();
      // Only cache successful responses — don't poison the TTL with error bodies
      // (401/500 etc. would otherwise block retries for 60s after server recovery).
      if (res.ok && ttlMs > 0) {
        cache.set(url, { value: data, expiry: Date.now() + ttlMs });
      }
      return data;
    } finally {
      inFlight.delete(url);
    }
  })();

  inFlight.set(url, promise);
  return promise;
}

export function invalidateDedupedFetch(url: string): void {
  cache.delete(url);
  inFlight.delete(url);
}

// Wipe all cached + in-flight entries. Call on logout / auth-boundary changes
// so a returning user never sees a previous user's cached data.
export function invalidateAllDedupedFetches(): void {
  cache.clear();
  inFlight.clear();
}
